import toast from "@/lib/toast";
import TeamService from "@/services/api/team";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import {
  createAsyncThunk,
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from "@reduxjs/toolkit";

interface LoadingState {
  export: boolean;
  checkInClaims: boolean;
  attendance: boolean;
  userLocation: boolean;
  callLogs: boolean;
  timeline: boolean;
  meeting: boolean;
  logs: boolean;
  lateCheckIn: boolean;
  deviceChangeLogs: boolean;
  teams: boolean;
  userSettings: boolean;
  userCheckInClaim: boolean;
  globalSettings: boolean;
  teamclaimsLoading: boolean;
}

export interface MarkerData {
  position: { lat: number; lng: number };
  userName: string;
  checkInTime: string;
  checkOutTime: string;
  address: string;
  type: string;
  trackingDate?: string;
  leadId?: any;
  leadName?: string;
}
interface TeamState {
  teams: any[];
  attendances: any[];
  holidays: any[];
  meetings: any[];
  geoDetails: any[];
  markers: MarkerData[];
  locations: any[];
  callLogs: any[];
  timelines: any[];
  logs: any[];
  lateCheckIns: {
    Items: any[];
    TotalRecords: number;
  } | null;
  activityDashboard: any;
  userReports: any;
  globalSettings: any;
  regularization: any;
  workstationList: any[];
  isFetching: boolean;
  error: string | null;
  inActiveUser: any[];
  managers: any[];
  userSettings: any[];
  geoFenceType: any[];
  userSettingById: any[];
  checkInClaims: any[];
  userCheckInClaim: any[];
  adminUsers: any[];
  geoFencingArea: any[];
  deviceChangeLogs: any[];
  geoUserDetail: any[];
  teamClaimsLogs: any[];
  loading: LoadingState;
}

const initialState: TeamState = {
  teams: [],
  attendances: [],
  holidays: [],
  meetings: [],
  geoDetails: [],
  markers: [],
  locations: [],
  callLogs: [],
  timelines: [],
  logs: [],
  inActiveUser: [],
  managers: [],
  lateCheckIns: null,
  activityDashboard: null,
  userReports: null,
  globalSettings: {},
  regularization: null,
  workstationList: [],
  isFetching: false,
  error: null,
  userSettings: [],
  geoFenceType: [],
  userSettingById: [],
  checkInClaims: [],
  userCheckInClaim: [],
  adminUsers: [],
  geoFencingArea: [],
  deviceChangeLogs: [],
  geoUserDetail: [],
  teamClaimsLogs: [],
  loading: {
    export: false,
    checkInClaims: false,
    attendance: false,
    userLocation: false,
    callLogs: false,
    timeline: false,
    meeting: false,
    logs: false,
    lateCheckIn: false,
    deviceChangeLogs: false,
    teams: false,
    userSettings: false,
    userCheckInClaim: false,
    globalSettings: false,
    teamclaimsLoading: false
  },
};

export const getMyTeamGCDetail = createAsyncThunk(
  "team/getMyTeamGCDetail",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getMyTeamGCDetail(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch myteam gc detail"
      );
    }
  }
);

export const getCallLogsByUserId = createAsyncThunk(
  "team/getCallLogsByUserId",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getCallLogsByUserId(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch call logs");
    }
  }
);

export const getTimeline = createAsyncThunk(
  "team/getTimeline",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getTimeline(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch timeline");
    }
  }
);

export const getUserActivityDashboard = createAsyncThunk(
  "team/getUserActivityDashboard",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await TeamService.getUserActivityDashboard(userId);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch user activity");
    }
  }
);

export const getUserReport = createAsyncThunk(
  "team/getUserReport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getUserReport(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch user report");
    }
  }
);

export const getMeetings = createAsyncThunk(
  "team/getMeetings",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getMeetings(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch meetings");
    }
  }
);

export const getUpcomingMeetings = createAsyncThunk(
  "team/getUpcomingMeetings",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getUpcomingMeetings(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch upcoming meetings"
      );
    }
  }
);

export const getDailyAttendanceReport = createAsyncThunk(
  "team/getDailyAttendanceReport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getDailyAttendanceReport(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch daily attendance report"
      );
    }
  }
);

export const getLogs = createAsyncThunk(
  "team/getLogs",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getLogs(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch logs");
    }
  }
);

export const getTeamWiseGlobalMapData = createAsyncThunk(
  "team/getTeamWiseGlobalMapData",
  async (date: string, { rejectWithValue }) => {
    try {
      const response = await TeamService.getTeamWiseGlobalMapData(date);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch teamwise global map data"
      );
    }
  }
);

export const getUserWiseGlobalMapData = createAsyncThunk(
  "team/getUserWiseGlobalMapData",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getUserWiseGlobalMapData(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch userwise global map data"
      );
    }
  }
);

export const getUserLocation = createAsyncThunk(
  "team/getUserLocation",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getUserLocation(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch user location");
    }
  }
);

export const userCheckInClaims = createAsyncThunk(
  "team/userCheckInClaims",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.userCheckInClaims(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch user check-in claims"
      );
    }
  }
);
export const updateAttendanceRegularization = createAsyncThunk(
  "team/updateAttendanceRegularization",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.updateAttendanceRegularization(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch attendance regularization");
    }
  }
);

export const getRegularizationHistory = createAsyncThunk(
  "team/getRegularizationHistory",
  async (checkInId: number, { rejectWithValue }) => {
    try {
      const response = await TeamService.getRegularizationHistory(checkInId);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch regularization history");
    }
  }
);

export const getGeoFencingArea = createAsyncThunk(
  "team/getGeoFencingArea",
  async (_, { rejectWithValue }) => {
    try {
      const response = await TeamService.getGeoFencingArea();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch geofencing area");
    }
  }
);

export const getGeoFenceType = createAsyncThunk(
  "team/getGeoFenceType",
  async (_, { rejectWithValue }) => {
    try {
      const response = await TeamService.getGeoFenceType();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch geofencetype");
    }
  }
);

export const getUserGlobalSetting = createAsyncThunk(
  "team/getUserGlobalSetting",
  async (_, { rejectWithValue }) => {
    try {
      const response = await TeamService.getUserGlobalSetting();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch user global setting"
      );
    }
  }
);

export const getWorkstation = createAsyncThunk(
  "team/getWorkstation",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getWorkstation(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch workstation");
    }
  }
);

export const getCheckInClaims = createAsyncThunk(
  "team/getCheckInClaims",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getCheckInClaims(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch checkin claims");
    }
  }
);

export const updateCheckInTime = createAsyncThunk(
  "team/updateCheckInTime",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.updateCheckInTime(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      console.error("Error in updateCheckInTime:", err);
      return rejectWithValue(err?.response?.data?.Message || "Failed to update checkintime");
    }
  }
);

export const updateStatusOfRequestCheckInTime = createAsyncThunk(
  "team/updateStatusOfRequestCheckInTime",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.updateStatusOfRequestCheckInTime(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      console.error("Error in updateStatusOfRequestCheckInTime:", err);
      return rejectWithValue(err?.response?.data?.Message || "Failed to update checkintime request status");
    }
  }
);

export const exportUserNotPunchedInReport = createAsyncThunk(
  "team/exportUserNotPunchedInReport",
  async (_, { rejectWithValue }) => {
    try {
      const response = await TeamService.exportUserNotPunchedInReport();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch export report");
    }
  }
);

export const exportAttendanceReport = createAsyncThunk(
  "team/exportAttendanceReport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.exportAttendanceReport(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to download attendance report");
    }
  }
);

export const getManagers = createAsyncThunk(
  "team/getManagers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await TeamService.getManagers();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch managers");
    }
  }
);

export const getInactiveUsers = createAsyncThunk(
  "team/getInactiveUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await TeamService.getInactiveUsers();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch inactive users");
    }
  }
);

export const getGlobalMapData = createAsyncThunk(
  "team/getGlobalMapData",
  async (date: string, { rejectWithValue }) => {
    try {
      const response = await TeamService.getGlobalMapData(date);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch global map data");
    }
  }
);

export const sendUserReport = createAsyncThunk(
  "team/sendUserReport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.sendUserReport(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to send user report");
    }
  }
);

export const sendAllUsersReport = createAsyncThunk(
  "team/sendAllUsersReport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.sendAllUsersReport(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to send all users report");
    }
  }
);

export const sendAllUsersDetailsReport = createAsyncThunk(
  "team/sendAllUsersDetailsReport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.sendAllUsersDetailsReport(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to send all users details report");
    }
  }
);

export const getUserSetting = createAsyncThunk(
  "team/getUserSetting",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await TeamService.getUserSetting(userId);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch user setting");
    }
  }
);

export const updateUserSetting = createAsyncThunk(
  "team/updateUserSetting",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.updateUserSetting(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to update user setting");
    }
  }
);

export const getAdminUsers = createAsyncThunk(
  "team/getAdminUsers",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await TeamService.getAdminUsers(userId);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch admin users");
    }
  }
);

export const updateNotifyAdmin = createAsyncThunk(
  "team/updateNotifyAdmin",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.updateNotifyAdmin(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to update notify admin");
    }
  }
);

export const GetAllUserSettingsList = createAsyncThunk(
  "team/getAllUserSettingsList",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getAllUserSettingsList(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch all user setting list");
    }
  }
);

export const getLateCheckInList = createAsyncThunk(
  "team/getLateCheckInList",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.lateCheckIn(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch late checkin list"
      );
    }
  }
);

export const updateLateCheckInStatus = createAsyncThunk(
  "team/updateLateCheckInStatus",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.updateLateCheckInStatus(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to update late checkin status"
      );
    }
  }
);

export const getDeviceChangeLogs = createAsyncThunk(
  "team/getDeviceChangeLogs",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getDeviceChangeLogs(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch device change logs");
    }
  }
);

export const getDeviceChangeLogsExport = createAsyncThunk(
  "team/getDeviceChangeLogsExport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getDeviceChangeLogsExport(data);
      return saveAsExcelFile(response, "DeviceChangeLogs");
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to export",
      );
    }
  },
);

export const getDailyStatusReport = createAsyncThunk("team/downloadDailyStatusReport",
  async (data: { id: number, date: any }, { rejectWithValue }) => {
    try {
      const response = await TeamService.getDailyStatusReport(data);
      return response;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message)
    }
  }
);

export const getLeadImage = createAsyncThunk(
  "team/getLeadImage",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await TeamService.getLeadImage(id);
      return response;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to load image")
    }
  }
);

export const getGeoUserDetail = createAsyncThunk(
  "team/getGeoUserDetail",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await TeamService.getGeoUserDetail(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch geo user detail")
    }
  }
);

export const getClaimsLogs = createAsyncThunk(
  "team/getClaimsLogs",
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await TeamService.getTeamsClaimsLogs(id);
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to load image")
    }
  }
)

const teamSlice = createSlice({
  name: "team",
  initialState,
  reducers: {
    updateMarkerAddress: (state, action) => {
      const { index, address } = action.payload;
      if (state.markers[index]) {
        state.markers[index].address = address;
      }
    },
  },
  extraReducers: (builder) => {
    // specific updates
    builder
      .addCase(getMyTeamGCDetail.pending, (state) => {
        state.teams = [];
        state.loading.teams = true;
      })
      .addCase(getMyTeamGCDetail.fulfilled, (state, action) => {
        state.teams = action.payload || [];
        state.loading.teams = false;
      })
      .addCase(getMyTeamGCDetail.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.teams = false;
      })

      .addCase(getDailyAttendanceReport.pending, (state) => {
        state.attendances = [];
        state.loading.attendance = true;
      })
      .addCase(getDailyAttendanceReport.fulfilled, (state, action) => {
        state.attendances = action.payload || [];
        state.loading.attendance = false;
      })
      .addCase(getDailyAttendanceReport.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.attendance = false;
      })

      .addCase(getUserGlobalSetting.pending, (state) => {
        state.loading.globalSettings = true;
      })
      .addCase(getUserGlobalSetting.fulfilled, (state, action) => {
        state.loading.globalSettings = false;
        state.globalSettings = action.payload || {};
      })
      .addCase(getUserGlobalSetting.rejected, (state, action) => {
        state.loading.globalSettings = false;
        state.error = action.payload as string;
      })

      .addCase(getTeamWiseGlobalMapData.pending, (state) => {
        state.error = null;
        state.markers = [];
      })
      .addCase(getTeamWiseGlobalMapData.fulfilled, (state, action) => {
        const data = action.payload || [];
        state.markers = data
          .filter((item: any) => item.CheckInLatitude && item.CheckInLongitude)
          .map((item: any) => ({
            position: {
              lat: item.CheckInLatitude,
              lng: item.CheckInLongitude,
            },
            userName: item.UserName,
            checkInTime: item.CheckInTime,
            checkOutTime: item.CheckOutTime,
            address: "",
          }));
      })
      .addCase(getGlobalMapData.fulfilled, (state, action) => {
        const data = action.payload || [];
        state.markers = data
          .filter((item: any) => item.CheckInLatitude && item.CheckInLongitude)
          .map((item: any) => ({
            position: {
              lat: item.CheckInLatitude,
              lng: item.CheckInLongitude,
            },
            userName: item.UserName,
            checkInTime: item.CheckInTime,
            checkOutTime: item.CheckOutTime,
            address: "",
          }));

      })

      .addCase(getUserWiseGlobalMapData.pending, (state) => {
        state.error = null;
        state.markers = [];
      })
      .addCase(getGeoFencingArea.fulfilled, (state, action) => {
        state.geoFencingArea = action.payload || [];
      })
      .addCase(getUserWiseGlobalMapData.fulfilled, (state, action) => {
        const data = action.payload || {};
        state.markers = [];
        if (data.CheckInLatitude && data.CheckInLongitude) {
          state.markers.push({
            position: {
              lat: data.CheckInLatitude,
              lng: data.CheckInLongitude,
            },
            userName: data.UserName,
            checkInTime: data.CheckInTime,
            checkOutTime: data.CheckOutTime,
            address: "",
            type: "punchIn",
          });
        }

        if (data.CheckOutLatitude && data.CheckOutLongitude) {
          state.markers.push({
            position: {
              lat: data.CheckOutLatitude,
              lng: data.CheckOutLongitude,
            },
            userName: data.UserName,
            checkInTime: data.CheckInTime,
            checkOutTime: data.CheckOutTime,
            address: "",
            type: "punchOut",
          });
        }

        if (data.userlatlng.length > 0) {
          for (let i = 0; i < data.userlatlng.length; i++) {
            state.markers.push({
              position: {
                lat: data.userlatlng[i].Latitude,
                lng: data.userlatlng[i].Longitude,
              },
              userName: data.UserName,
              checkInTime: data.CheckInTime,
              checkOutTime: data.CheckOutTime,
              address: "",
              trackingDate: data.userlatlng[i].TrackingDate,
              type: "trackingUser",
              leadId: data.userlatlng[i].LeadId,
              leadName: data.userlatlng[i].LeadName,
            });
          }
        }
      })

      .addCase(getUserLocation.pending, (state) => {
        state.locations = [];
        state.loading.userLocation = true;
      })
      .addCase(getUserLocation.fulfilled, (state, action) => {
        state.locations = action.payload || [];
        state.loading.userLocation = false;
      })
      .addCase(getUserLocation.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.userLocation = false;
      })

      .addCase(getCallLogsByUserId.pending, (state) => {
        state.callLogs = [];
        state.loading.callLogs = true;
      })
      .addCase(getCallLogsByUserId.fulfilled, (state, action) => {
        state.callLogs = action.payload || [];
        state.loading.callLogs = false;
      })
      .addCase(getCallLogsByUserId.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.callLogs = false;
      })

      .addCase(getTimeline.pending, (state) => {
        state.timelines = [];
        state.loading.timeline = true;
      })
      .addCase(getTimeline.fulfilled, (state, action) => {
        state.timelines = action.payload || [];
        state.loading.timeline = false;
      })
      .addCase(getTimeline.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.timeline = false;
      })

      .addCase(getUserActivityDashboard.fulfilled, (state, action) => {
        state.activityDashboard = action.payload;
      })
      .addCase(getUserReport.fulfilled, (state, action) => {
        state.userReports = action.payload;
      })

      .addCase(getMeetings.pending, (state) => {
        state.meetings = [];
        state.loading.meeting = true;
      })
      .addCase(getMeetings.fulfilled, (state, action) => {
        state.meetings = action.payload || [];
        state.loading.meeting = false;
      })
      .addCase(getMeetings.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.meeting = false;
      })

      .addCase(getUpcomingMeetings.pending, (state) => {
        state.meetings = [];
        state.loading.meeting = true;
      })
      .addCase(getUpcomingMeetings.fulfilled, (state, action) => {
        state.meetings = action.payload || [];
        state.loading.meeting = false;
      })
      .addCase(getUpcomingMeetings.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.meeting = false;
      })

      .addCase(getLogs.pending, (state) => {
        state.logs = [];
        state.loading.logs = true;
      })
      .addCase(getLogs.fulfilled, (state, action) => {
        state.logs = action.payload || [];
        state.loading.logs = false;
      })
      .addCase(getLogs.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.logs = false;
      })

      .addCase(getLateCheckInList.pending, (state) => {
        state.lateCheckIns = null;
        state.loading.lateCheckIn = true;
      })
      .addCase(getLateCheckInList.fulfilled, (state, action) => {
        state.lateCheckIns = action.payload || [];
        state.loading.lateCheckIn = false;
      })
      .addCase(getLateCheckInList.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.lateCheckIn = false;
      })

      .addCase(getInactiveUsers.fulfilled, (state, action) => {
        state.inActiveUser = action.payload || [];
      })
      .addCase(getManagers.fulfilled, (state, action) => {
        state.managers = action.payload || [];
      })
      .addCase(getGeoFenceType.fulfilled, (state, action) => {
        state.geoFenceType = action.payload || [];
      })

      .addCase(GetAllUserSettingsList.pending, (state) => {
        state.userSettings = [];
        state.loading.userSettings = true;
      })
      .addCase(GetAllUserSettingsList.fulfilled, (state, action) => {
        state.userSettings = action.payload || [];
        state.loading.userSettings = false;
      })
      .addCase(GetAllUserSettingsList.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.userSettings = false;
      })

      .addCase(getUserSetting.fulfilled, (state, action) => {
        state.userSettingById = action.payload || [];
      })
      .addCase(getCheckInClaims.pending, (state) => {
        state.loading.checkInClaims = true;
      })
      .addCase(getCheckInClaims.fulfilled, (state, action) => {
        state.checkInClaims = action.payload || [];
        state.loading.checkInClaims = false;
      })
      .addCase(getCheckInClaims.rejected, (state, action) => {
        state.error = action.payload as string;
        state.checkInClaims = [];
        state.loading.checkInClaims = false;
      })

      .addCase(userCheckInClaims.pending, (state) => {
        state.userCheckInClaim = [];
        state.loading.userCheckInClaim = true;
      })
      .addCase(userCheckInClaims.fulfilled, (state, action) => {
        state.userCheckInClaim = action.payload || [];
        state.loading.userCheckInClaim = false;
      })
      .addCase(userCheckInClaims.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.userCheckInClaim = false;
      })

      .addCase(getAdminUsers.fulfilled, (state, action) => {
        state.adminUsers = action.payload || [];
      })
      .addCase(updateLateCheckInStatus.fulfilled, (state, action) => {
        const updatedItem = action.payload;

        if (!updatedItem?.Id || !state.lateCheckIns) return;

        const index = state.lateCheckIns.Items.findIndex(
          (item: any) => item.Id === updatedItem.Id
        );

        if (index !== -1) {
          state.lateCheckIns.Items[index] = {
            ...state.lateCheckIns.Items[index],
            Status: updatedItem.Status,
          };
        }
      })
      .addCase(getRegularizationHistory.fulfilled, (state, action) => {
        state.regularization = action.payload;
      })
      .addCase(getWorkstation.fulfilled, (state, action) => {
        state.workstationList = action.payload || [];
      })

      .addCase(getDeviceChangeLogs.pending, (state) => {
        state.deviceChangeLogs = [];
        state.loading.deviceChangeLogs = true;
      })
      .addCase(getDeviceChangeLogs.fulfilled, (state, action) => {
        state.deviceChangeLogs = action.payload || [];
        state.loading.deviceChangeLogs = false;
      })
      .addCase(getDeviceChangeLogs.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.deviceChangeLogs = false;
      })

      .addCase(getDeviceChangeLogsExport.pending, (state) => {
        state.loading.export = true;
      })
      .addCase(getDeviceChangeLogsExport.fulfilled, (state, action) => {
        state.loading.export = false;
      })
      .addCase(getDeviceChangeLogsExport.rejected, (state, action) => {
        state.loading.export = false;
        state.error = action.payload as string;
      })

      .addCase(getGeoUserDetail.pending, (state) => {
        state.geoUserDetail = [];
      })
      .addCase(getGeoUserDetail.fulfilled, (state, action) => {
        state.geoUserDetail = action.payload || [];
      })
      .addCase(getGeoUserDetail.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(getClaimsLogs.pending, (state) => {
        state.teamClaimsLogs = [];
        state.loading.teamclaimsLoading = true;
      })
      .addCase(getClaimsLogs.fulfilled, (state, action) => {
        state.teamClaimsLogs = action.payload || [];
        state.loading.teamclaimsLoading = false;
      })
      .addCase(getClaimsLogs.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading.teamclaimsLoading = false;
      });

    // Generic loading & error
    builder
      .addMatcher(isPending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state) => {
        state.isFetching = false;
      })
      .addMatcher(isRejected, (state, action: any) => {
        state.isFetching = false;
        state.error = action.payload || "Something went wrong";
      });
  },
});

export const { updateMarkerAddress } = teamSlice.actions;
export default teamSlice.reducer;
